import * as Highcharts from 'highcharts'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import { DashboardData } from '@/domain/dashboard.types'

import theme from '../../../infrastructure/themes/theme'
import { CustomHighchartPoint, GraphData, GraphPoint, GraphSerie } from './graph.types'

const roundGraphPoint = (point: GraphPoint) => [
  point[0],
  point[1] === null ? null : Math.round(point[1]),
]

const textUnavailableData = (
  chart: Highcharts.Chart,
  textArray: Highcharts.SVGElement[],
  isMobile: boolean
) => {
  chart.series.forEach((serie) => {
    serie.points.forEach((point: CustomHighchartPoint) => {
      if (point.plotX && point.y === null) {
        const text = chart.renderer.text('N/A', 0, 0).add()
        textArray.push(text)
        text.attr({
          x: point.plotX + chart.plotLeft,
          y: chart.plotTop + chart.plotHeight - 10,
          fill: `${theme.colors.whiteAlpha40}`,
          rotation: -90,
          style: isMobile ? 'font-size: 8px;' : '',
        })
      }
    })
  })
}

export const getTootltipFormat = (interval: number): string => {
  switch (true) {
    case interval >= 43200:
      return 'MMMM'
    case interval >= 1440:
      return 'dd/MM'
    case interval < 1440:
      return 'HH:mm'
    default:
      return 'HH:mm'
  }
}

/**
 * Format data to graph points
 * @param data
 * @returns
 */
export const formatDataToGraphPoints = (data: DashboardData[]): GraphPoint[] => {
  return data.map((point: { epoch: number; value: number }) => [point.epoch, point.value])
}

/**
 * Build graph options
 * @param chartData
 * @param units
 * @param interval
 * @param isMobile
 */
const buildOptions = (
  chartData: GraphData[],
  units: string,
  interval: number,
  isMobile: boolean
): Highcharts.Options => {
  let textOverlayElts: Highcharts.SVGElement[] = []
  const series: GraphSerie[] = chartData.map((element) => ({
    name: element.name,
    type: element.type,
    lineWidth: isMobile ? 1 : 3,
    data: element.points.map(roundGraphPoint),
    marker: {
      enabled: false,
    },
    pointRange: interval * 60000,
  }))
  const options: Highcharts.Options = {
    time: {
      useUTC: false,
    },
    title: {
      text: undefined,
    },
    chart: {
      backgroundColor: theme.colors.transparent,
      events: {
        load() {
          textOverlayElts.forEach((elt) => elt.destroy())
          textOverlayElts = []
          textUnavailableData(this, textOverlayElts, isMobile)
        },
        redraw() {
          textOverlayElts.forEach((elt) => elt.destroy())
          textOverlayElts = []
          textUnavailableData(this, textOverlayElts, isMobile)
        },
      },
    },
    credits: {
      enabled: false,
    },
    colors: [theme.colors.primaryColor500, theme.colors.secondaryColor500],
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      floating: false,
      symbolPadding: 20,
      itemStyle: {
        fontFamily: 'Work sans',
        fontSize: '14px',
        color: theme.colors.whiteAlpha70,
      },
      itemHoverStyle: {
        color: theme.colors.white,
      },
    },
    xAxis: {
      type: 'datetime',
      tickLength: 0,
      tickInterval: interval >= 1440 ? interval * 60 * 1000 : undefined,
      lineColor: theme.colors.whiteAlpha20,
      lineWidth: 2,
      labels: {
        formatter() {
          return format(new Date(this.value), getTootltipFormat(interval), { locale: fr })
        },
        style: {
          fontFamily: 'Work sans',
          fontSize: isMobile ? '10px' : '12px',
          fontWeight: '700',
          color: theme.colors.whiteAlpha40,
        },
      },
    },
    yAxis: {
      title: {
        align: 'middle',
        text: units,
        style: {
          fontFamily: 'Work sans',
          fontSize: isMobile ? '10px' : '14px',
          fontWeight: '700',
          color: theme.colors.whiteAlpha40,
        },
      },
      gridLineColor: theme.colors.transparent,
      lineColor: theme.colors.whiteAlpha20,
      lineWidth: 2,
      labels: {
        style: {
          fontFamily: 'Work sans',
          fontSize: isMobile ? '10px' : '14px',
          fontWeight: '700',
          color: theme.colors.whiteAlpha40,
        },
      },
    },
    plotOptions: {
      column: {
        borderWidth: 0,
      },
    },
    series,
  }

  let tooltipOption: Highcharts.TooltipOptions = {
    distance: 20,
    shared: true,
    backgroundColor: theme.colors.blackAlpha80,
    borderColor: theme.colors.whiteAlpha80,
    headerFormat: `<div style="display: block">{point.key}</div><br>`,
    style: {
      color: theme.colors.white,
      fontSize: '12px',
      fontFamily: 'Work sans',
    },
    padding: 10,
    valueSuffix: ` ${units}`,
    dateTimeLabelFormats: {
      second: '%d/%m/%Y %H:%M:%S',
      minute: '%d/%m/%Y %H:%M',
      hour: '%d/%m/%Y %H:%M',
      day: '%d/%m/%Y',
      week: '%e. %b',
      month: "%b '%y",
      year: '%Y',
    },
  }

  if (interval >= 43200) {
    tooltipOption = { ...tooltipOption, xDateFormat: '%B %Y' }
  }

  return { ...options, tooltip: tooltipOption }
}

export default buildOptions
